/**
 * SOLID Review
 * S — Strong. One concern: the Framework as a component — its two sockets, held by its holder — and the
 *     React context that hands what they resolve to (and two host resources) to the components.
 * O — Good. A new host resource is one more field of `IConfigDepFrameworkHost`; a new port is the holder's.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. The context carries the two ports' interfaces and two host resources — nothing else.
 * D — Good. The kernel's holder, the Application's port types and the host resources' contracts; the
 *     component is connected by the composition root (`FrontendConnectors`), never by itself.
 */
import React, { createContext, useContext, useState, type ReactNode } from 'react';
import { ComponentFrontendFrameworkRequiredPorts } from './ComponentFrontendFrameworkRequiredPorts';
import type { IPortClientUseCases } from '../../application/ports/ClientUseCases.contract';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IServiceLiveKitAdapter } from '../../application/ports/IGatewayLiveKit';

/** The websocket's state, read live — AppRoot's last-resort recovery watchdog asks it after a return. */
export interface IServiceWsConnection {
  readonly getState: () => string;
}

/**
 * What the host hands the Framework (rule 2.3: host resources are not sockets). Both are main-thread
 * browser objects the Application has no business holding:
 *
 *   - `liveKitClientFactory`: the LAZY real-time LiveKit client factory — calling `create()` dynamic-imports
 *     the SDK, so only the hook that knows the player opted into video invokes it. Genuinely null when
 *     absent: AppRoot decides from it whether video exists at all, and a Null factory would make an absent
 *     capability look present (§28.30).
 *   - `wsConnectionMonitor`: the socket's state RIGHT NOW. The table's `connectionState` follows the
 *     socket's changes while the table is watched, but a watch hears changes only — not the state at
 *     subscribe time — so the watchdog's "is it still down?" needs the live read.
 */
export interface IConfigDepFrameworkHost {
  readonly liveKitClientFactory: IServiceLiveKitAdapter | null;
  readonly wsConnectionMonitor: IServiceWsConnection;
}

/** No host: no video, and a socket that reports itself down. */
const NO_HOST: IConfigDepFrameworkHost = { liveKitClientFactory: null, wsConnectionMonitor: { getState: () => 'disconnected' } };

/** What every component under the provider can reach — and nothing else. */
export interface IServiceComponentFrameworkState extends IConfigDepFrameworkHost {
  /** The component itself, so a consumer can reach the PORT (to walk the graph for a diagram). */
  readonly component: ComponentFrontendFramework;
  /** What React may ask of the Application — every move, catch-up and read (strict-ports FE1–FE5). */
  readonly clientUseCases: IPortClientUseCases;
  /** The client state, by reference, for `useSyncExternalStore` — what React renders from (FE1). */
  readonly clientStateView: IPortClientStateView;
}

const ComponentFrameworkContext = createContext<IServiceComponentFrameworkState | null>(null);

export function useComponentFramework(): IServiceComponentFrameworkState {
  const ctx = useContext(ComponentFrameworkContext);
  if (!ctx) {
    throw new Error('useComponentFramework must be used within <ComponentFrameworkProvider>');
  }
  return ctx;
}

/**
 * THE FRONTEND'S FRAMEWORK AS A COMPONENT, ENCLOSED (frontend strict-ports plan, FE6a, 2026-10-04) —
 * shaped like the backend's `ComponentFramework`: `MODEL_NAME`, one required holder (it provides nothing),
 * connected by the composition root's connector table like every other component.
 *
 * WHAT WENT. Until FE6 this component carried eleven legacy adapter sockets beside its two ports — the
 * adapter objects React called directly, each with a Null fallback — connected ITSELF from an
 * `IAdapterRegistry` on every provider mount (`connect()`/`wire()`, the overturned §28.77 rule), and the
 * provider resolved them in an async "bootstrap" with an auth-token, credits and local-user state nothing
 * read. FE4 and FE5 moved every flow behind `ClientUseCases` and deleted six sockets; FE6a deletes the last
 * five (Room, Game, Session, RoomLookup, WsConnectionMonitor — no production reader was left but the
 * watchdog's live socket read, which is a host resource now), the registry, the self-connection and the
 * bootstrap. `ComponentFrontendFrameworkRequiredPorts` holds exactly `ClientUseCases` and `ClientStateView`.
 *
 * Every socket is `RequiredPortWithDefault`: a UI degrades rather than throwing (§28.21; §28.33 is the
 * alternative in production). `frontend-no-throwing-port` enforces it.
 */
export class ComponentFrontendFramework {
  /** The model name in every generated diagram — one literal, so a typo cannot split this box in two. */
  static readonly MODEL_NAME = 'ComponentFrontendFramework';

  /** Its two sockets — the backend's holder shape. The Framework provides nothing. */
  readonly requiredPorts = new ComponentFrontendFrameworkRequiredPorts();
}

export interface IPropsComponentFrameworkProvider {
  readonly children: ReactNode;
  /**
   * The ASSEMBLED Framework component — the composition root's (`frontendRoot`), its sockets connected by
   * `FrontendConnectors` with the rest of the graph. Absent: an unconnected one, whose sockets answer from
   * their defaults (a test's provider, or a misconfiguration that must degrade, not white-screen).
   */
  readonly framework?: ComponentFrontendFramework;
  readonly host?: IConfigDepFrameworkHost;
}

/**
 * The context, resolved ONCE per mount (useState's initialiser runs on the first render only): the two
 * ports' interfaces — read through the sockets, so their contracts guard every call — and the host
 * resources. No async bootstrap: nothing to resolve but what the composition root assembled.
 */
export const ComponentFrameworkProvider: React.FC<IPropsComponentFrameworkProvider> = ({ children, framework, host = NO_HOST }) => {
  const [state] = useState<IServiceComponentFrameworkState>(() => {
    const component = framework ?? new ComponentFrontendFramework();
    const resolved: IServiceComponentFrameworkState = {
      component,
      clientUseCases: component.requiredPorts.clientUseCasesSocket.getInterface(),
      clientStateView: component.requiredPorts.clientStateViewSocket.getInterface(),
      liveKitClientFactory: host.liveKitClientFactory,
      wsConnectionMonitor: host.wsConnectionMonitor,
    };
    return resolved;
  });

  return (
    <ComponentFrameworkContext.Provider value={state}>
      {children}
    </ComponentFrameworkContext.Provider>
  );
};
